import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import { SectionHeader } from './SectionHeader';
import type { DeviceStatus } from '@/lib/types';

interface Props { device: DeviceStatus; disabled: boolean }

const ARM_SECONDS = 10;

export function FactoryResetSection({ device, disabled }: Props) {
  const [armed, setArmed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(ARM_SECONDS);

  const mut = useDeviceMutation<{ confirm: 'RESET' }>(
    device.client_id, 'factory-reset', { successToast: 'Factory reset queued — device will reboot' },
  );

  // Auto-disarm countdown
  useEffect(() => {
    if (!armed) return;
    setSecondsLeft(ARM_SECONDS);
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(t); setArmed(false); return ARM_SECONDS; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [armed]);

  const onClick = () => {
    if (!armed) { setArmed(true); return; }
    setArmed(false);
    mut.mutate({ confirm: 'RESET' });
  };

  return (
    <div className="space-y-5">
      <SectionHeader title="Factory reset"
        description="Erases commissioning data (identity, relay map, modules, flow config) and reboots the device into AP commissioning mode. Schedule, actuation times, and the last-cleaning record are preserved." />

      <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
        <strong>This is destructive.</strong> The device will need re-commissioning after reset.
      </div>

      <Button
        variant={armed ? 'danger' : 'outline'}
        onClick={onClick}
        disabled={disabled || mut.isPending}
        loading={mut.isPending}
        className={armed ? '!border-red-700' : '!border-red-300 !text-red-700 hover:!bg-red-50'}
      >
        <Trash2 className="w-4 h-4" />
        {armed ? `Click again to CONFIRM (${secondsLeft}s)` : 'Factory reset'}
      </Button>
    </div>
  );
}
